import { DATE_FORMATS } from "@/constants";
import { createMeditationRetreatRequestSchema } from "@/constants/zod-schemas";
import { useGetMeditationRetreatLevels } from "@/hooks/query-hooks/useLevels";
import { calculateDuration } from "@/libs/utils";
import { DatePicker, Form, FormInstance, FormProps, Input, InputNumber, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";

type FormValuesType = {
    levelId: number;
    title: string;
    period: NoUndefinedRangeValueType<Dayjs>;
    duration: number;
    maxYogiLimit: number;
}

interface MeditationRetreatFormType extends FormProps {
    onSubmit: (values: CreateMeditationRetreatRequest) => void,
    form: FormInstance
}

type NoUndefinedRangeValueType<DataType> = [start: DataType | null, end: DataType | null];


export function MeditationRetreatForm({
    onSubmit,
    form,
    ...props
}: MeditationRetreatFormType) {

    const { data: levels } = useGetMeditationRetreatLevels();

    const onDatePickerChange = (dates: NoUndefinedRangeValueType<Dayjs> | null) => {
        if (dates && dates[0] && dates[1]) {
            const daysDiff = calculateDuration(dates[0], dates[1]);
            form.setFieldValue("duration", daysDiff);
        }
    };

    const onFinish = (values: FormValuesType) => {
        const { period, ...rest } = values;
        const [startDate, endDate] = period;
        const data: CreateMeditationRetreatRequest = {
            ...rest,
            fromDate: dayjs(startDate).format(DATE_FORMATS.REQUEST_DATE_FORMAT),
            toDate: dayjs(endDate).format(DATE_FORMATS.REQUEST_DATE_FORMAT),
        };

        const payload = createMeditationRetreatRequestSchema.parse(data)

        onSubmit(payload);
    }

    return (
        <Form
            form={form}
            onFinish={onFinish}
            {...props}
            layout="vertical"
            className="!pt-4"
        >
            <div
                className="grid grid-cols-2 gap-6"
            >
                <Form.Item
                    label="တရားစခန်းအဆင့်"
                    name="levelId"
                    rules={[{ required: true, message: "Please input level!" }]}
                >
                    <Select
                        options={levels?.map((level) => ({
                            label: level.name,
                            value: level.id,
                        }))}
                        placeholder="Level"
                        size="large"
                    />
                </Form.Item>
                <Form.Item
                    label="တရားစခန်းအမည်"
                    name="title"
                    rules={[{ required: true, message: "Please input title!" }]}
                >
                    <Input
                        placeholder="Title"
                        size="large"
                    />
                </Form.Item>
            </div>

            <div
                className="grid grid-cols-2 gap-6"
            >
                <Form.Item
                    label="ကျင်းပမည့်အချိန်"
                    name="period"
                    rules={[{ required: true, message: "Please input period!" }]}
                >
                    <DatePicker.RangePicker
                        disabledDate={(current) => current && current < dayjs().startOf("day").add(1, "day")}
                        inputReadOnly
                        onChange={onDatePickerChange}
                        size="large"
                    />
                </Form.Item>

                <Form.Item
                    label="ကြာချိန်"
                    name="duration"
                    dependencies={["period"]}
                >
                    <Input
                        placeholder="Duration"
                        disabled
                        suffix="days"
                        size="large"
                    />
                </Form.Item>
            </div>
            <div
                className="grid grid-cols-2 gap-6"
            >
                <Form.Item
                    label="လက်ခံမည့် ယောဂီအရေအတွက်ဦးရေ"
                    name="maxYogiLimit"
                    rules={[{ required: true, type: "number", min: 1, message: "လက်ခံမည့် ယောဂီအရေအတွက်ကို ထည့်ပါ!" }]}
                >
                    <InputNumber
                        style={{ width: "100%" }}
                        placeholder="ယောဂီအရေအတွက်"
                        min={1}
                        size="large"
                    />
                </Form.Item>
            </div>
        </Form >
    )
}
